<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import { apiErrorMessage } from '../../../../api/coLiving/errors';
import { FIELD_CLASS, SELECT_CLASS } from '../constants';

const MAX_MINUTES = 100000;

const isMinutes = (raw, min) =>
  raw !== '' &&
  Number.isInteger(Number(raw)) &&
  Number(raw) >= min &&
  Number(raw) <= MAX_MINUTES;

export default {
  name: 'TierModal',
  components: { BaseModal },
  props: {
    tier: {
      type: Object,
      required: true,
    },
  },
  emits: ['close'],
  data() {
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      form: {
        basis: this.tier.basis,
        ack: this.tier.ack,
        eta: this.tier.eta,
        escalate: this.tier.escalate,
        fallback_role: this.tier.fallback_role || '',
      },
      submitted: false,
      isSubmitting: false,
    };
  },
  computed: {
    ...mapGetters('coLivingConfig', { roles: 'getActiveRoles' }),
    // T0 always runs 24x7: the server refuses anything else.
    isUrgent() {
      return this.tier.tier === 'T0';
    },
    fieldErrors() {
      return {
        ack: !isMinutes(this.form.ack, 1),
        eta: !isMinutes(this.form.eta, 1),
        escalate: !isMinutes(this.form.escalate, 0),
      };
    },
    isFormValid() {
      return !Object.values(this.fieldErrors).some(Boolean);
    },
  },
  methods: {
    ...mapActions('coLivingConfig', ['saveSlaTier']),
    async onSubmit() {
      this.submitted = true;
      if (!this.isFormValid) return;
      this.isSubmitting = true;
      try {
        await this.saveSlaTier({
          tier: this.tier.tier,
          fields: {
            basis: this.isUrgent ? '24x7' : this.form.basis,
            ack: Number(this.form.ack),
            eta: Number(this.form.eta),
            escalate: Number(this.form.escalate),
            fallback_role: this.form.fallback_role || null,
          },
        });
        useAlert(this.$t('CO_LIVING.COMMON.SAVED'));
        this.$emit('close');
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'CO_LIVING.SLA.SAVE_FAILED'));
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <BaseModal @close="$emit('close')">
    <template #title>
      {{ $t('CO_LIVING.SLA.EDIT_TITLE', { tier: tier.tier }) }}
    </template>

    <form
      class="grid grid-cols-1 gap-4"
      data-testid="co-living-tier-form"
      @submit.prevent="onSubmit"
    >
      <div>
        <label
          for="co-living-tier-basis"
          class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
        >
          {{ $t('CO_LIVING.SLA.FIELDS.BASIS') }}
        </label>
        <select
          id="co-living-tier-basis"
          v-model="form.basis"
          :class="selectClass"
          :disabled="isUrgent"
          data-testid="co-living-tier-basis"
        >
          <option value="24x7">{{ $t('CO_LIVING.SLA.BASIS.24X7') }}</option>
          <option value="working">
            {{ $t('CO_LIVING.SLA.BASIS.WORKING') }}
          </option>
        </select>
        <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {{ $t('CO_LIVING.SLA.FIELDS.BASIS_HINT') }}
        </p>
      </div>

      <div>
        <label
          for="co-living-tier-ack"
          class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
        >
          {{ $t('CO_LIVING.SLA.FIELDS.ACK') }}
        </label>
        <input
          id="co-living-tier-ack"
          v-model="form.ack"
          type="number"
          min="1"
          :class="[
            fieldClass,
            submitted && fieldErrors.ack ? 'border-red-500' : '',
          ]"
          data-testid="co-living-tier-ack"
        />
        <p
          v-if="submitted && fieldErrors.ack"
          class="mt-1 text-xs text-red-500"
        >
          {{ $t('CO_LIVING.SLA.ERRORS.MINUTES', { min: 1 }) }}
        </p>
      </div>

      <div>
        <label
          for="co-living-tier-eta"
          class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
        >
          {{ $t('CO_LIVING.SLA.FIELDS.ETA') }}
        </label>
        <input
          id="co-living-tier-eta"
          v-model="form.eta"
          type="number"
          min="1"
          :class="[
            fieldClass,
            submitted && fieldErrors.eta ? 'border-red-500' : '',
          ]"
          data-testid="co-living-tier-eta"
        />
        <p
          v-if="submitted && fieldErrors.eta"
          class="mt-1 text-xs text-red-500"
        >
          {{ $t('CO_LIVING.SLA.ERRORS.MINUTES', { min: 1 }) }}
        </p>
      </div>

      <div>
        <label
          for="co-living-tier-escalate"
          class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
        >
          {{ $t('CO_LIVING.SLA.FIELDS.ESCALATE') }}
        </label>
        <input
          id="co-living-tier-escalate"
          v-model="form.escalate"
          type="number"
          min="0"
          :class="[
            fieldClass,
            submitted && fieldErrors.escalate ? 'border-red-500' : '',
          ]"
          data-testid="co-living-tier-escalate"
        />
        <p
          v-if="submitted && fieldErrors.escalate"
          class="mt-1 text-xs text-red-500"
        >
          {{ $t('CO_LIVING.SLA.ERRORS.MINUTES', { min: 0 }) }}
        </p>
        <p v-else class="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {{ $t('CO_LIVING.SLA.FIELDS.ESCALATE_HINT') }}
        </p>
      </div>

      <div>
        <label
          for="co-living-tier-fallback"
          class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
        >
          {{ $t('CO_LIVING.SLA.FIELDS.FALLBACK_ROLE') }}
        </label>
        <select
          id="co-living-tier-fallback"
          v-model="form.fallback_role"
          :class="selectClass"
          data-testid="co-living-tier-fallback"
        >
          <option value="">{{ $t('CO_LIVING.COMMON.NONE') }}</option>
          <option v-for="role in roles" :key="role.key" :value="role.key">
            {{ role.label }}
          </option>
        </select>
      </div>

      <div class="flex justify-end gap-3">
        <woot-button variant="clear" type="button" @click="$emit('close')">
          {{ $t('CO_LIVING.COMMON.CANCEL') }}
        </woot-button>
        <woot-button
          type="submit"
          :is-loading="isSubmitting"
          data-testid="co-living-tier-save"
        >
          {{ $t('CO_LIVING.COMMON.SAVE') }}
        </woot-button>
      </div>
    </form>
  </BaseModal>
</template>
